<template>
  <div class="orders-container mx-lg-4 mx-2">
    <div v-if="showPostOrderModal" class="post-order-modal-backdrop" @click="closePostOrderModal">
      <div class="post-order-modal" @click.stop>
        <div class="post-order-modal-icon">
          <i class="fas fa-envelope-open-text"></i>
        </div>
        <h3 class="post-order-modal-title">Detalle enviado por email</h3>
        <p class="post-order-modal-text">
          Tu pedido #{{ postOrderEmailNotice.orderId }} ya figura en esta sección.
          {{ postOrderEmailNotice.email ? `Enviamos el detalle de compra a ${postOrderEmailNotice.email}.` : 'Enviamos el detalle de compra a tu email registrado.' }}
        </p>
        <button class="post-order-modal-btn" @click="closePostOrderModal">Entendido</button>
      </div>
    </div>

    <div v-if="showShippingInfoModal" class="post-order-modal-backdrop" @click="closeShippingInfoModal">
      <div class="shipping-info-modal" @click.stop>
        <div class="shipping-info-header">
          <h3 class="shipping-info-title">Info de envío - Pedido #{{ selectedShippingOrder?.id }}</h3>
          <button class="shipping-close-btn" type="button" @click="closeShippingInfoModal">
            <i class="fas fa-times"></i>
          </button>
        </div>
        <div class="shipping-info-body" v-if="selectedShippingOrder">
          <div class="shipping-data-grid">
            <div class="shipping-data-item">
              <span class="shipping-data-label">Destinatario</span>
              <span class="shipping-data-value">{{ selectedShippingOrder.recipient_name || '-' }}</span>
            </div>
            <div class="shipping-data-item">
              <span class="shipping-data-label">Email</span>
              <span class="shipping-data-value">{{ selectedShippingOrder.customer_email || '-' }}</span>
            </div>
            <div class="shipping-data-item">
              <span class="shipping-data-label">Teléfono</span>
              <span class="shipping-data-value">{{ selectedShippingOrder.customer_phone || '-' }}</span>
            </div>
            <div class="shipping-data-item">
              <span class="shipping-data-label">Dirección</span>
              <span class="shipping-data-value">{{ selectedShippingOrder.shipping_address || '-' }}</span>
            </div>
            <div class="shipping-data-item">
              <span class="shipping-data-label">Servicio</span>
              <span class="shipping-data-value">{{ selectedShippingOrder.shipping_product_name || selectedShippingOrder.shipping_service || '-' }}</span>
            </div>
            <div class="shipping-data-item">
              <span class="shipping-data-label">Costo</span>
              <span class="shipping-data-value">
                {{ selectedShippingOrder.shipping_cost != null ? `$${formatPrice(selectedShippingOrder.shipping_cost)}` : '-' }}
              </span>
            </div>
            <div class="shipping-data-item" v-if="selectedShippingOrder.shipping_estimated_min || selectedShippingOrder.shipping_estimated_max">
              <span class="shipping-data-label">Entrega estimada</span>
              <span class="shipping-data-value">
                {{ selectedShippingOrder.shipping_estimated_min || '?' }} - {{ selectedShippingOrder.shipping_estimated_max || '?' }} días
              </span>
            </div>
            <div class="shipping-data-item" v-if="selectedShippingOrder.shipping_postal_code">
              <span class="shipping-data-label">Código postal</span>
              <span class="shipping-data-value">{{ selectedShippingOrder.shipping_postal_code }}</span>
            </div>
          </div>
          <div class="shipping-tracking-block">
            <ShipmentTracking :order-id="selectedShippingOrder.id" />
          </div>
        </div>
      </div>
    </div>

    <div v-if="showSupportModal" class="post-order-modal-backdrop" @click="closeSupportModal">
      <div class="support-modal" @click.stop>
        <div class="shipping-info-header">
          <h3 class="shipping-info-title">Soporte - Pedido #{{ selectedSupportOrder?.id }}</h3>
          <button class="shipping-close-btn" type="button" @click="closeSupportModal">
            <i class="fas fa-times"></i>
          </button>
        </div>
        <div class="support-modal-body">
          <label class="support-label" for="supportMessage">Mensaje</label>
          <textarea
            id="supportMessage"
            v-model.trim="supportMessage"
            class="support-textarea"
            rows="5"
            maxlength="2000"
            placeholder="Escribi tu consulta para soporte sobre este pedido"
          ></textarea>
          <div class="support-help-row">
            <span>Soporte recibira los datos del pedido junto con tu mensaje.</span>
            <span>{{ supportMessage.length }}/2000</span>
          </div>
        </div>
        <div class="support-actions">
          <button class="support-cancel-btn" type="button" @click="closeSupportModal">Cancelar</button>
          <button
            class="support-send-btn"
            type="button"
            :disabled="sendingSupportMessage || supportMessage.length < 10"
            @click="sendSupportMessage"
          >
            <i class="fas" :class="sendingSupportMessage ? 'fa-spinner fa-spin' : 'fa-paper-plane'"></i>
            {{ sendingSupportMessage ? 'Enviando...' : 'Enviar a soporte' }}
          </button>
        </div>
      </div>
    </div>

    <!-- Header -->
    <div class="orders-header d-lg-flex flex-wrap justify-content-between align-items-center mb-4 gap-3">
      <div>
        <h2 class="page-title">Mis Pedidos</h2>
        <div class="mt-2">
          <router-link to="/configuracion/mi-cuenta" class="btn-account-link">
            <i class="fas fa-user-cog me-2"></i>Configurar mi cuenta
          </router-link>
        </div>
      </div>
      <div class="d-flex flex-wrap align-items-center gap-2 order-filters-group">
        <button @click="refreshOrders" class="btn-refresh d-flex align-items-center gap-1" :disabled="loading">
          <i class="fas fa-sync-alt" :class="{'fa-spin': loading}"></i>
          <span>Actualizar</span>
        </button>
        <span class="count-badge d-none d-md-inline">{{ filteredOrders.length }} pedidos</span>
      </div>
    </div>

    <!-- Loading State -->
    <div v-if="loading" class="text-center py-5">
      <div class="spinner-border mb-3" role="status">
        <span class="visually-hidden">Cargando...</span>
      </div>
      <div class="loading-text">Cargando tus pedidos...</div>
    </div>

    <!-- No Orders State -->
    <div v-else-if="!orders.length" class="text-center py-5 empty-orders">
      <i class="fas fa-box-open fa-3x mb-3 empty-icon"></i>
      <p class="empty-text">No tienes pedidos realizados aún</p>
      <router-link to="/productos" class="btn-go-shop mt-2">
        <i class="fas fa-store me-2"></i>Ir a la tienda
      </router-link>
    </div>

    <!-- Orders List -->
    <div v-else class="orders-list">
      <div v-for="order in filteredOrders" :key="order.id" class="order-card">
        <!-- Order Header -->
        <div class="order-header d-flex flex-wrap justify-content-between align-items-center mb-2 gap-2">
          <div class="d-flex align-items-center gap-2 flex-wrap">
            <span class="order-number">#{{ order.id }}</span>
            <span :class="['status-badge', order.status]">
              <i class="fas fa-circle me-1"></i>{{ getStatusText(order.status) }}
            </span>
            <div class="delivery-payment-info d-flex gap-2 flex-wrap">
              <span class="delivery-type">
                <i class="fas" :class="getDeliveryIcon(order.delivery_type)"></i>
                {{ getDeliveryText(order.delivery_type) }}
              </span>
            </div>
          </div>
          <div class="d-flex align-items-center gap-2">
            <button class="btn-support-order" type="button" @click="openSupportModal(order)">
              <i class="fas fa-headset me-1"></i>
              Soporte
            </button>
            <span class="order-date">
              <i class="far fa-calendar-alt me-1"></i>
              {{ formatDate(order.created_at) }}
            </span>
            <button class="btn-toggle d-md-none" @click="order.showDetails = !order.showDetails">
              <i :class="order.showDetails ? 'fas fa-chevron-up' : 'fas fa-chevron-down'"></i>
            </button>
          </div>
        </div>

        <!-- Order Details (productos siempre visibles, sin debug) -->
        <div class="order-details">
          <div class="customer-info mb-2">
            <div class="info-item">
              <i class="fas fa-user me-2"></i>
              <span>{{ order.recipient_name }}</span>
            </div>
            <div class="info-item">
              <i class="fas fa-envelope me-2"></i>
              <span>{{ order.customer_email }}</span>
            </div>
            <div class="info-item">
              <i class="fas fa-phone me-2"></i>
              <span>{{ order.customer_phone }}</span>
            </div>
            <div class="info-item" v-if="order.shipping_address && order.delivery_type !== 'delivery'">
              <i class="fas fa-map-marker-alt me-2"></i>
              <span>{{ order.shipping_address }}</span>
            </div>
            <div v-if="order.delivery_type === 'delivery'" class="info-item">
              <i class="fas fa-truck me-2"></i>
              <button type="button" class="shipping-link-btn" @click="openShippingInfoModal(order)">
                Ver info de envío
              </button>
            </div>
          </div>

          <!-- Products List (siempre visible) -->
          <div class="products-list-wrapper">
            <div v-if="order.products && order.products.length" class="products-list" :class="{'scroll-x': $screen('sm')}" :style="$screen('sm') ? 'overflow-x:auto;display:flex;gap:1rem;' : ''">
              <div v-for="product in order.products" :key="product.id" class="product-item">
                <div class="product-image">
                  <img 
                    :src="product.product_details?.image || require('@/assets/images/logo.png')" 
                    :alt="product.product_details?.name || 'Product image'" 
                    class="order-image"
                    style="object-fit: contain;"
                  >
                </div>
                <div class="product-info">
                  <h6 class="product-name">{{ product.product_details?.name || 'Producto sin nombre' }}</h6>
                  <div class="product-details">
                    <span class="quantity">Cantidad: {{ product.quantity }}</span>
                    <span class="price">${{ formatPrice(product.price_at_time) }} c/u</span>
                  </div>
                  <div class="subtotal">
                    Subtotal: ${{ formatPrice(product.quantity * parseFloat(product.price_at_time)) }}
                  </div>
                </div>
              </div>
            </div>
            <div v-else style="color: #ff6b6b; font-size: 14px;">No hay productos en esta orden.</div>
            <div class="products-summary mt-2 text-end text-md-start">
              <span class="products-count">Total productos: <b>{{ order.products ? order.products.length : 0 }}</b></span>
            </div>
          </div>
        </div>

        <!-- Order Footer -->
        <div class="order-footer mt-2">
          <button
            v-if="canPayPendingOrder(order)"
            type="button"
            class="btn-pay-mercadopago"
            :disabled="payingOrderId === order.id"
            @click="payPendingOrder(order)"
          >
            <i class="fas" :class="payingOrderId === order.id ? 'fa-spinner fa-spin' : 'fa-credit-card'"></i>
            {{ payingOrderId === order.id ? 'Redirigiendo...' : 'Pagar con MercadoPago' }}
          </button>
          <div class="total">
            <div class="total-breakdown">
              <div class="total-row" v-if="Number(order.shipping_cost || 0) > 0">
                <span>Envio:</span>
                <span>${{ formatPrice(order.shipping_cost) }}</span>
              </div>
              <div class="total-row total-row-final">
                <span class="total-label">Total del pedido:</span>
                <span class="total-amount">${{ formatPrice(getOrderTotalARS(order)) }}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue';
import { useToast } from 'vue-toastification';
import { getUserOrders, contactOrder } from '@/services/orders';
import { createPayment, getCurrentDollarRate } from '@/services/payment';
import ShipmentTracking from '@/components/shipping/ShipmentTracking.vue';

const toast = useToast();
const orders = ref([]);
const loading = ref(true);
const statusFilter = ref('');
const dollarRate = ref(0);
const showPostOrderModal = ref(false);
const postOrderEmailNotice = ref({ orderId: null, email: '' });
const showShippingInfoModal = ref(false);
const selectedShippingOrder = ref(null);
const payingOrderId = ref(null);
const showSupportModal = ref(false);
const selectedSupportOrder = ref(null);
const supportMessage = ref('');
const sendingSupportMessage = ref(false);

// Responsive screen helper
const $screen = (size) => {
  if (typeof window === 'undefined') return false;
  const breakpoints = { sm: 600, md: 900, lg: 1200 };
  return window.innerWidth < breakpoints[size];
};

const filteredOrders = computed(() => {
  if (!statusFilter.value) return orders.value;
  return orders.value.filter(order => order.status === statusFilter.value);
});

const getStatusText = (status) => {
  const statusMap = {
    'pending': 'Pendiente',
    'paid': 'Pagado',
    'shipped': 'Enviado',
    'delivered': 'Entregado',
    'cancelled': 'Cancelado'
  };
  return statusMap[status] || status;
};

const formatDate = (dateString) => {
  return new Date(dateString).toLocaleDateString('es-ES', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
};

const refreshOrders = async () => {
  localStorage.removeItem('userOrders');
  localStorage.removeItem('ordersTimestamp');
  await fetchOrders();
  toast.success('Pedidos actualizados');
};

const fetchOrders = async () => {
  try {
    loading.value = true;
    const token = localStorage.getItem('accessToken');
    if (!token) {
      toast.error('No has iniciado sesión');
      loading.value = false;
      return;
    }
    const response = await getUserOrders();
    if (response && Array.isArray(response)) {
      // Añadir propiedad showDetails para colapsar en mobile
      orders.value = response.map(o => ({ ...o, showDetails: false }));
    } else {
      orders.value = [];
      console.warn('La respuesta no es un array:', response);
    }
  } catch (error) {
    console.error('Error al cargar los pedidos:', error);
    toast.error('Error al cargar los pedidos: ' + (error.message || 'Error desconocido'));
    orders.value = [];
  } finally {
    loading.value = false;
  }
};

const getDeliveryIcon = (type) => {
  return type === 'delivery' ? 'fa-truck' : 'fa-store';
};

const getDeliveryText = (type) => {
  return type === 'delivery' ? 'Envío a domicilio' : 'Retiro en tienda';
};

const formatPrice = (price) => {
  return parseFloat(price).toFixed(2);
};

const closePostOrderModal = () => {
  showPostOrderModal.value = false;
  sessionStorage.removeItem('post_order_email_notice');
};

const openShippingInfoModal = (order) => {
  selectedShippingOrder.value = order;
  showShippingInfoModal.value = true;
};

const closeShippingInfoModal = () => {
  showShippingInfoModal.value = false;
  selectedShippingOrder.value = null;
};

const openSupportModal = (order) => {
  selectedSupportOrder.value = order;
  supportMessage.value = '';
  showSupportModal.value = true;
};

const closeSupportModal = () => {
  if (sendingSupportMessage.value) return;
  showSupportModal.value = false;
  selectedSupportOrder.value = null;
  supportMessage.value = '';
};

const sendSupportMessage = async () => {
  if (!selectedSupportOrder.value || supportMessage.value.length < 10) {
    toast.error('Escribi un mensaje de al menos 10 caracteres');
    return;
  }

  try {
    sendingSupportMessage.value = true;
    await contactOrder(selectedSupportOrder.value.id, supportMessage.value);
    toast.success('Mensaje enviado a soporte');
    sendingSupportMessage.value = false;
    closeSupportModal();
  } catch (error) {
    toast.error(error.message || 'Error al enviar el mensaje a soporte');
  } finally {
    sendingSupportMessage.value = false;
  }
};

const canPayPendingOrder = (order) => {
  const pendingStatuses = ['pending', 'payment_pending'];
  const paymentMethod = (order.payment_method || '').toLowerCase();
  const isOnlinePayment = paymentMethod === 'online' || paymentMethod === 'mercadopago' || paymentMethod === 'mp';
  return pendingStatuses.includes(order.status) && isOnlinePayment;
};

const payPendingOrder = async (order) => {
  try {
    payingOrderId.value = order.id;
    const paymentResponse = await createPayment(order.id);

    if (!paymentResponse || !paymentResponse.init_point) {
      throw new Error('No se pudo generar el link de pago.');
    }

    toast.success('Redirigiendo a MercadoPago...');
    setTimeout(() => {
      window.location.href = paymentResponse.init_point;
    }, 600);
  } catch (error) {
    toast.error(error.message || 'Error al iniciar el pago.');
  } finally {
    payingOrderId.value = null;
  }
};

function getOrderTotalARS(order) {
  if (order.total != null) return Number(order.total) || 0;
  if (!order.products) return 0;
  return order.products.reduce((total, product) => {
    const currency = product.product_details?.currency || 'ARS';
    const price = product.price_at_time;
    const qty = product.quantity;
    if (currency === 'USD' && dollarRate.value) {
      return total + (price * qty * dollarRate.value);
    } else {
      return total + (price * qty);
    }
  }, 0) - Number(order.discount || 0) - Number(order.bulk_promotion_discount || 0) + Number(order.shipping_cost || 0);
}


onMounted(async () => {
  try {
    const notice = sessionStorage.getItem('post_order_email_notice');
    if (notice) {
      postOrderEmailNotice.value = JSON.parse(notice);
      showPostOrderModal.value = true;
    }
  } catch (error) {
    console.error('Error leyendo aviso post pedido:', error);
  }

  await fetchOrders();
  try {
    const rate = await getCurrentDollarRate();
    if (rate && rate.value) {
      dollarRate.value = parseFloat(rate.value);
    }
  } catch (e) {
    dollarRate.value = 0;
  }
});
</script>

<style scoped>
.orders-container {
  margin: 0 auto;
}

.post-order-modal-backdrop {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.65);
  z-index: 1100;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1rem;
}

.post-order-modal {
  width: min(460px, 100%);
  background: #ffffff;
  border-radius: 14px;
  border: 1px solid #d1d5db;
  box-shadow: 0 22px 50px rgba(15, 23, 42, 0.35);
  padding: 1.4rem 1.2rem;
  text-align: center;
}

.post-order-modal-icon {
  width: 56px;
  height: 56px;
  margin: 0 auto 0.8rem;
  border-radius: 50%;
  background: var(--success-bg);
  color: var(--success-color);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.4rem;
}

.post-order-modal-title {
  margin: 0 0 0.45rem;
  color: #1f2937;
  font-size: 1.1rem;
  font-weight: 700;
}

.post-order-modal-text {
  margin: 0 0 1rem;
  color: #4b5563;
  line-height: 1.5;
}

.post-order-modal-btn {
  border: none;
  border-radius: 8px;
  background: var(--primary-color);
  color: #fff;
  font-weight: 600;
  padding: 0.55rem 1.1rem;
  cursor: pointer;
}

.post-order-modal-btn:hover {
  background: #115e59;
}

.shipping-info-modal {
  width: min(620px, 100%);
  max-height: 88vh;
  overflow-y: auto;
  background: var(--black-color-3, #1c1c1c);
  border-radius: 14px;
  border: 1px solid rgba(255, 255, 255, 0.12);
  box-shadow: 0 22px 50px rgba(15, 23, 42, 0.35);
  padding: 1rem 1rem 1.2rem;
}

.shipping-info-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.8rem;
  margin-bottom: 0.8rem;
}

.shipping-info-title {
  margin: 0;
  color: #f9fafb;
  font-size: 1rem;
  font-weight: 700;
}

.shipping-close-btn {
  border: none;
  background: rgba(255, 255, 255, 0.08);
  width: 32px;
  height: 32px;
  border-radius: 8px;
  color: #f9fafb;
  cursor: pointer;
}

.shipping-data-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: 0.65rem;
}

.shipping-data-item {
  background: rgba(0, 0, 0, 0.3);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 8px;
  padding: 0.55rem 0.65rem;
  display: flex;
  flex-direction: column;
  gap: 0.15rem;
}

.shipping-data-label {
  color: #9ca3af;
  font-size: 0.75rem;
  text-transform: uppercase;
  letter-spacing: 0.03em;
}

.shipping-data-value {
  color: #f9fafb;
  font-size: 0.88rem;
  font-weight: 600;
  word-break: break-word;
}

.shipping-tracking-block {
  margin-top: 0.8rem;
  border-top: 1px solid rgba(255, 255, 255, 0.12);
  padding-top: 0.8rem;
}

.shipping-link-btn {
  border: none;
  background: transparent;
  color: var(--primary-light);
  font-size: 0.82rem;
  font-weight: 600;
  padding: 0;
  text-decoration: underline;
  cursor: pointer;
}

.shipping-link-btn:hover {
  color: #6cd3a0;
}

.support-modal {
  width: min(520px, 100%);
  background: var(--black-color-3, #1c1c1c);
  border-radius: 14px;
  border: 1px solid rgba(255, 255, 255, 0.12);
  box-shadow: 0 22px 50px rgba(15, 23, 42, 0.35);
  padding: 1rem;
}

.support-modal-body {
  display: flex;
  flex-direction: column;
  gap: 0.45rem;
}

.support-label {
  color: #d1d5db;
  font-size: 0.82rem;
  font-weight: 600;
}

.support-textarea {
  width: 100%;
  min-height: 130px;
  background: rgba(0, 0, 0, 0.35);
  border: 1px solid rgba(255, 255, 255, 0.12);
  border-radius: 8px;
  color: #f9fafb;
  padding: 0.75rem;
  resize: vertical;
  outline: none;
}

.support-textarea:focus {
  border-color: var(--primary-light);
  box-shadow: 0 0 0 0.18rem rgba(var(--primary-light-rgb), 0.16);
}

.support-help-row {
  display: flex;
  justify-content: space-between;
  gap: 0.75rem;
  color: #888;
  font-size: 0.76rem;
}

.support-actions {
  display: flex;
  justify-content: flex-end;
  gap: 0.6rem;
  margin-top: 1rem;
}

.support-cancel-btn,
.support-send-btn,
.btn-support-order {
  border-radius: 8px;
  font-size: 0.8rem;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.35rem;
  cursor: pointer;
  transition: all 0.15s ease;
}

.support-cancel-btn {
  border: 1px solid rgba(255, 255, 255, 0.12);
  background: rgba(0, 0, 0, 0.2);
  color: #aaa;
  padding: 0.48rem 0.85rem;
}

.support-send-btn {
  border: none;
  background: var(--primary-color);
  color: #fff;
  font-weight: 700;
  padding: 0.5rem 0.9rem;
}

.support-send-btn:hover:not(:disabled) {
  background: var(--primary-dark);
}

.support-send-btn:disabled {
  opacity: 0.55;
  cursor: not-allowed;
}

.btn-support-order {
  border: 1px solid rgba(var(--primary-light-rgb), 0.35);
  background: rgba(var(--primary-color-rgb), 0.14);
  color: var(--primary-light);
  padding: 0.22rem 0.6rem;
  font-weight: 700;
}

.btn-support-order:hover {
  background: rgba(var(--primary-color-rgb), 0.24);
  border-color: rgba(var(--primary-light-rgb), 0.55);
}

.page-title {
  color: #ccc;
  font-size: 1.4rem;
  font-weight: 600;
  margin: 0;
}

.btn-account-link {
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  padding: 0.3rem 0.85rem;
  background: rgba(0,0,0,0.2);
  border: 1px solid rgba(255,255,255,0.08);
  border-radius: 8px;
  color: #aaa;
  font-size: 0.82rem;
  text-decoration: none;
  transition: all 0.15s ease;
}
.btn-account-link:hover {
  background: rgba(var(--primary-color-rgb), 0.2);
  border-color: rgba(var(--primary-light-rgb), 0.3);
  color: var(--primary-light);
}

.btn-refresh {
  padding: 0.3rem 0.85rem;
  background: rgba(0,0,0,0.2);
  border: 1px solid rgba(255,255,255,0.08);
  border-radius: 8px;
  color: #aaa;
  font-size: 0.84rem;
  transition: all 0.15s ease;
  cursor: pointer;
}
.btn-refresh:hover:not(:disabled) {
  background: rgba(var(--primary-color-rgb), 0.2);
  border-color: rgba(var(--primary-light-rgb), 0.3);
  color: var(--primary-light);
}
.btn-refresh:disabled { opacity: 0.5; cursor: not-allowed; }

.count-badge {
  padding: 0.18rem 0.65rem;
  background: rgba(0,0,0,0.2);
  border: 1px solid rgba(255,255,255,0.07);
  border-radius: 20px;
  color: #888;
  font-size: 0.8rem;
}

.spinner-border { color: var(--primary-light); }

.loading-text { color: #888; font-size: 0.84rem; }

.empty-icon { color: #555; }
.empty-text { color: #888; font-size: 0.95rem; }

.btn-go-shop {
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  padding: 0.45rem 1.1rem;
  background: var(--primary-color);
  border: none;
  border-radius: 8px;
  color: #fff;
  font-size: 0.84rem;
  text-decoration: none;
  transition: background 0.15s ease;
}
.btn-go-shop:hover { background: var(--primary-dark); color: #fff; }

.orders-header { padding: 10px 0; }

.order-card {
  background: rgba(0,0,0,0.18);
  border: 1px solid rgba(255,255,255,0.07);
  border-radius: 10px;
  margin-bottom: 14px;
  padding: 16px 14px 12px;
  color: #ccc;
  transition: border-color 0.15s ease;
}
.order-card:hover { border-color: rgba(255,255,255,0.11); }

.order-number {
  font-weight: 600;
  color: var(--primary-light);
  font-size: 0.95rem;
}

.status-badge {
  padding: 0.18rem 0.75rem;
  border-radius: 20px;
  font-size: 0.78rem;
  font-weight: 600;
  display: inline-flex;
  align-items: center;
  gap: 0.35em;
}
.status-badge.pending { background: rgba(251,191,36,0.2); color: #fbbf24; }
.status-badge.payment_pending { background: rgba(248,113,113,0.2); color: #f87171; }
.status-badge.paid { background: rgba(var(--success-color-rgb), 0.2); color: var(--success-color); }
.status-badge.shipped { background: rgba(96,165,250,0.2); color: #60a5fa; }
.status-badge.delivered { background: rgba(var(--success-color-rgb), 0.25); color: var(--success-color); }
.status-badge.cancelled { background: rgba(248,113,113,0.18); color: #f87171; }

.delivery-type, .payment-method {
  font-size: 0.8rem;
  color: #888;
  background: rgba(0,0,0,0.15);
  border: 1px solid rgba(255,255,255,0.06);
  padding: 0.18rem 0.55rem;
  border-radius: 6px;
  display: inline-flex;
  align-items: center;
  gap: 0.3rem;
}

.order-date { color: #888; font-size: 0.8rem; }

.btn-toggle {
  padding: 0.25rem 0.55rem;
  background: rgba(0,0,0,0.2);
  border: 1px solid rgba(255,255,255,0.07);
  border-radius: 6px;
  color: #aaa;
  font-size: 0.78rem;
  cursor: pointer;
  transition: all 0.15s ease;
}
.btn-toggle:hover { background: rgba(var(--primary-color-rgb), 0.2); color: var(--primary-light); }

.customer-info {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(175px, 1fr));
  gap: 0.5rem;
  margin-bottom: 0.85rem;
}
.info-item {
  display: flex;
  align-items: center;
  color: #888;
  font-size: 0.82rem;
  gap: 0.4rem;
}
.info-item i { color: var(--primary-light); width: 14px; }

.products-list-wrapper { margin-bottom: 0.85rem; }
.products-list { display: flex; flex-direction: column; gap: 0.65rem; }
.products-list.scroll-x { flex-direction: row; overflow-x: auto; gap: 0.75rem; }

.product-item {
  display: flex;
  gap: 0.75rem;
  padding: 0.65rem;
  background: rgba(0,0,0,0.15);
  border: 1px solid rgba(255,255,255,0.05);
  border-radius: 8px;
  min-width: 250px;
  align-items: center;
}
.product-image { width: 60px; height: 60px; flex-shrink: 0; }
.product-image img {
  width: 100%; height: 100%;
  object-fit: cover;
  border-radius: 6px;
}
.product-info { flex: 1; }
.product-name {
  margin: 0 0 0.25rem;
  font-weight: 500;
  color: #ccc;
  font-size: 0.86rem;
}
.product-details {
  display: flex;
  gap: 0.75rem;
  color: #888;
  font-size: 0.8rem;
  margin-bottom: 0.15rem;
}
.subtotal { font-weight: 500; color: #aaa; font-size: 0.82rem; }
.products-count { color: #666; font-size: 0.8rem; }

.order-footer {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 0.75rem;
  margin-top: 0.65rem;
  padding-top: 0.65rem;
  border-top: 1px solid rgba(255,255,255,0.05);
}

.btn-pay-mercadopago {
  border: none;
  border-radius: 8px;
  background: #009ee3;
  color: #ffffff;
  font-weight: 700;
  font-size: 0.8rem;
  padding: 0.5rem 0.85rem;
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  cursor: pointer;
  transition: background 0.15s ease;
}

.btn-pay-mercadopago:hover:not(:disabled) {
  background: #018ac6;
}

.btn-pay-mercadopago:disabled {
  opacity: 0.7;
  cursor: not-allowed;
}
.total { display: flex; align-items: center; gap: 0.75rem; }
.total-breakdown { min-width: 210px; }
.total-row { display: flex; justify-content: space-between; gap: 1rem; color: #888; font-size: 0.82rem; }
.total-row-final { align-items: baseline; margin-top: 0.2rem; }
.total-label { font-weight: 500; color: #888; font-size: 0.82rem; }
.total-amount { font-size: 1.1rem; font-weight: 700; color: var(--primary-light); }

.empty-orders { display: flex; flex-direction: column; align-items: center; justify-content: center; }

@media (max-width: 900px) {
  .orders-container { padding: 8px; }
  .order-card { padding: 12px 8px; }
}
@media (max-width: 600px) {
  .order-card { padding: 10px 6px; }
  .product-item { flex-direction: column; align-items: flex-start; min-width: 160px; }
  .product-image { width: 100%; height: 90px; }
  .order-footer { flex-direction: column; gap: 0.5rem; align-items: flex-start; }
  .btn-pay-mercadopago { width: 100%; justify-content: center; }
  .btn-support-order { width: 100%; }
  .support-actions { flex-direction: column; }
  .support-cancel-btn, .support-send-btn { width: 100%; }
  .support-help-row { flex-direction: column; gap: 0.25rem; }
  .btn-go-shop, .btn-refresh, .btn-account-link { width: 100%; justify-content: center; }
  .products-list.scroll-x { min-width: 100vw; padding-bottom: 6px; }
}
.fade-enter-active, .fade-leave-active { transition: opacity 0.2s; }
.fade-enter-from, .fade-leave-to { opacity: 0; }
</style>

